/* ---------------------------------------------------------------------------
   Writes that have not reached the register yet.

   An inspection happens at the structure, and the structure is the one place
   with no signal. Every write used to go straight to /api and fail there, which
   meant the app worked everywhere except where it is used.

   **IndexedDB, not localStorage.** A queued inspection carries its photographs
   as base64 — a few hundred kilobytes each after the downscale, and up to eight
   of them. localStorage is a synchronous ~5 MB string store and would both
   overflow and block the main thread while it did.

   **Every queued write is idempotent.** Each carries a `clientId` minted on the
   device, and both endpoints treat a second arrival as success rather than as a
   duplicate. That is what makes replay safe, and it is why the queue can be
   simple: it never has to know whether an attempt that timed out actually
   landed, because sending it again costs nothing.

   **It only holds field capture.** Inspections, defects and construction
   entries — the things recorded at a riverbank or on a site. Inventory edits and
   model saves are not queued: they are done at a desk, they are last-write-wins
   against a record other people are editing, and a queued form edit replayed an
   hour later would silently overwrite work somebody did in between. That is a
   conflict story this does not have, so it does not offer the feature.

   **A construction entry is the case this was built for.** An inspection
   happens at a structure that already exists, where somebody may well have
   driven back into signal the same afternoon. A pour happens on a site that has
   no signal for the whole of the works, and the entry is made by somebody whose
   next opportunity to reach the register may be days away — which is why
   `occurredOn` is a field on the record rather than the time of the write, and
   why nothing here has to know how long an entry sat.

   **The storage is behind a seam** (`QueueStore`). IndexedDB in a browser, and
   an in-memory store under the tests — jsdom implements none of IndexedDB, and
   the alternative was either a dependency or leaving the whole queue unchecked.
   The seam is small and the browser implementation is the default.
   --------------------------------------------------------------------------- */

/**
 * The kinds of write this queue carries.
 *
 * FIELD CAPTURE ONLY, and the two site records that are not here say where the
 * line is. A DOCUMENT is not queued: it is up to eight megabytes, the queue
 * replays oldest-first, and one certificate photographed at full size would sit
 * in front of every pour recorded after it and fail the whole drain on a
 * connection that could not carry it. Closing an incident is not queued either
 * — it is an account of what was done, written later, at a desk, and nor is an
 * answer, close-out or release of a hold point, NCR or RFI. A test result and a
 * raised item ARE here (LC-53, LC-54): both are recorded on site.
 */
export type QueuedKind =
  | 'inspection'
  | 'defect'
  | 'progress'
  | 'site-return'
  | 'incident'
  | 'site-test'
  | 'site-item';

export interface QueuedWrite {
  /** Assigned by the store. The queue's own handle on the entry. */
  key?: number;
  kind: QueuedKind;
  /** Structure the write belongs to. */
  bridgeId: string;
  /** Device-minted id. What makes a replay safe. */
  clientId: string;
  /** The request body, exactly as it would have been posted. */
  body: Record<string, unknown>;
  queuedAt: string;
  attempts: number;
  /** Why the last attempt failed, for the queue panel. */
  lastError?: string;
}

/* --------------------------- the seam --------------------------- */

export interface QueueStore {
  add(entry: QueuedWrite): Promise<number>;
  all(): Promise<QueuedWrite[]>;
  put(entry: QueuedWrite): Promise<void>;
  remove(key: number): Promise<void>;
  clear(): Promise<void>;
}

const DB_NAME = 'bridge-watch';
const DB_VERSION = 1;
const STORE = 'writes';

function indexedDbStore(): QueueStore | null {
  try {
    if (typeof indexedDB === 'undefined' || indexedDB === null) return null;
  } catch {
    // Private-mode Firefox throws on the property access itself.
    return null;
  }

  let dbPromise: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'key', autoIncrement: true });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB refused to open.'));
    });
    return dbPromise;
  };

  const run = <T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> =>
    open().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const t = db.transaction(STORE, mode);
          const req = fn(t.objectStore(STORE));
          req.onsuccess = () => resolve(req.result as T);
          req.onerror = () => reject(req.error ?? new Error('IndexedDB write failed.'));
        }),
    );

  return {
    add: (entry) => run<IDBValidKey>('readwrite', (s) => s.add(entry)).then(Number),
    all: () => run<QueuedWrite[]>('readonly', (s) => s.getAll()),
    put: (entry) => run('readwrite', (s) => s.put(entry)).then(() => undefined),
    remove: (key) => run('readwrite', (s) => s.delete(key)).then(() => undefined),
    clear: () => run('readwrite', (s) => s.clear()).then(() => undefined),
  };
}

/** An in-memory store, for the tests and for reasoning about the logic. */
export function memoryStore(): QueueStore {
  let next = 1;
  let rows: QueuedWrite[] = [];
  return {
    add: async (entry) => {
      const key = next++;
      rows.push({ ...entry, key });
      return key;
    },
    all: async () => rows.map((r) => ({ ...r })),
    put: async (entry) => {
      rows = rows.map((r) => (r.key === entry.key ? { ...entry } : r));
    },
    remove: async (key) => {
      rows = rows.filter((r) => r.key !== key);
    },
    clear: async () => {
      rows = [];
    },
  };
}

let store: QueueStore | null | undefined;

function current(): QueueStore | null {
  if (store === undefined) store = indexedDbStore();
  return store;
}

/** Swap the backing store. Used by the tests; null means "no queue here". */
export function setQueueStore(next: QueueStore | null): void {
  store = next;
}

/**
 * Whether this browser will give us a queue at all.
 *
 * Private-mode Firefox and some locked-down profiles refuse IndexedDB. The app
 * keeps working there — it simply cannot queue, and says so rather than
 * appearing to save.
 */
export function queueAvailable(): boolean {
  return current() !== null;
}

/* --------------------------- the queue --------------------------- */

export async function enqueue(
  entry: Omit<QueuedWrite, 'key' | 'queuedAt' | 'attempts'>,
): Promise<QueuedWrite | null> {
  const s = current();
  if (!s) return null;
  const full: QueuedWrite = { ...entry, queuedAt: new Date().toISOString(), attempts: 0 };
  const key = await s.add(full);
  return { ...full, key };
}

/** Everything waiting, oldest first — which is the order it has to be sent in. */
export async function pending(): Promise<QueuedWrite[]> {
  const s = current();
  if (!s) return [];
  try {
    return (await s.all()).sort((a, b) => (a.key ?? 0) - (b.key ?? 0));
  } catch {
    return [];
  }
}

export async function clear(): Promise<void> {
  const s = current();
  if (!s) return;
  try {
    await s.clear();
  } catch {
    /* nothing to clear */
  }
}

/**
 * Whether a failure is worth retrying.
 *
 * A refusal is permanent: the register said no, and sending the same body again
 * will get the same no for ever while blocking everything behind it. Anything
 * else — no connection, a 502, a timeout — is the queue's whole reason to
 * exist. The distinction is the difference between a queue that drains and one
 * that jams on the first bad row.
 */
export function isPermanent(status: number | null): boolean {
  return status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

export type SendResult = { ok: true } | { ok: false; status: number | null; message: string };
export type Sender = (entry: QueuedWrite) => Promise<SendResult>;

export interface DrainResult {
  sent: number;
  failed: number;
  /** Entries dropped because the register refused them outright. */
  rejected: QueuedWrite[];
}

/**
 * Send a list of writes, oldest first.
 *
 * Separated from the storage so the decisions can be checked without a browser
 * — which of the three outcomes each answer produces is the whole of this
 * module's judgement, and it was otherwise reachable only through IndexedDB.
 *
 * Stops at the first retryable failure rather than working through the rest:
 * they are almost certainly going to fail the same way, and order matters —
 * two inspections on one structure should land in the order they were recorded.
 *
 * A permanent refusal is dropped and handed back, because the alternative is a
 * queue that never drains and an inspector who is never told why.
 */
export async function drainEntries(
  entries: readonly QueuedWrite[],
  send: Sender,
  onSent: (entry: QueuedWrite) => Promise<void>,
  onFailed: (entry: QueuedWrite, message: string) => Promise<void>,
): Promise<DrainResult> {
  const result: DrainResult = { sent: 0, failed: 0, rejected: [] };

  for (const entry of entries) {
    const outcome = await send(entry);

    if (outcome.ok) {
      await onSent(entry);
      result.sent++;
      continue;
    }

    if (isPermanent(outcome.status)) {
      await onSent(entry);
      result.rejected.push({ ...entry, lastError: outcome.message });
      continue;
    }

    await onFailed(entry, outcome.message);
    result.failed++;
    break;
  }

  return result;
}

/** Drain whatever is actually waiting. */
export async function drain(send: Sender): Promise<DrainResult> {
  const s = current();
  if (!s) return { sent: 0, failed: 0, rejected: [] };

  return drainEntries(
    await pending(),
    send,
    async (entry) => {
      if (entry.key !== undefined) await s.remove(entry.key);
    },
    async (entry, message) => {
      if (entry.key !== undefined) await s.put({ ...entry, attempts: entry.attempts + 1, lastError: message });
    },
  );
}
